import React, { useState, useEffect } from 'react';
import { Navbar, Nav, Container, Offcanvas, Badge, Form, Dropdown } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import logo from '../assets/prathitha-logo.png';
import { useCart } from '../context/CartContext';
import { searchableProducts } from '../data/searchableProducts';

interface AppNavbarProps {
  onGetQuote: () => void;
  isAuthenticated: boolean;
  onLogout: () => void;
}

export const formatDesignFileName = (fileNameStr: string | null) => {
  if (!fileNameStr) return '';
  if (fileNameStr.startsWith('{')) {
    try {
      const parsed = JSON.parse(fileNameStr);
      const parts = [];
      if (parsed.front) parts.push(`Front: ${parsed.front}`);
      if (parsed.back) parts.push(`Back: ${parsed.back}`);
      return parts.join(', ');
    } catch (_) {
      return fileNameStr;
    }
  }
  return fileNameStr;
};

const AppNavbar: React.FC<AppNavbarProps> = ({ onGetQuote, isAuthenticated, onLogout }) => {
  const [expanded, setExpanded] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity, cartTotal, cartCount, clearCart } = useCart();
  const role = localStorage.getItem('role') || 'customer';
  const dashboardPath = (role === 'admin' || role === 'manager' || role === 'admin-manager') ? '/admin' : '/my-orders';
  const dashboardLabel = (role === 'admin' || role === 'manager' || role === 'admin-manager') ? 'Admin Dashboard' : 'My Orders';

  const navItems: { label: string; path: string; icon: string }[] = [
    { label: 'Home', path: '/', icon: 'fas fa-home' },
    { label: 'About', path: '/about', icon: 'fas fa-info-circle' },
    { label: 'Products', path: '/products', icon: 'fas fa-print' },
    { label: 'Order Online', path: '/order', icon: 'fas fa-shopping-cart' },
    { label: 'Portfolio', path: '/portfolio', icon: 'fas fa-images' },
    { label: 'FAQ', path: '/faq', icon: 'fas fa-question-circle' },
    { label: 'Contact', path: '/contact', icon: 'fas fa-envelope' },
  ];

  const productCategories: { label: string; path: string }[] = [
    { label: 'All Products', path: '/products' },
    { label: 'Visiting Cards', path: '/products' },
    { label: 'Flyers & Brochures', path: '/products' },
    { label: 'Flex Printing', path: '/products' },
    { label: 'Stickers & Labels', path: '/products' },
    { label: 'Wedding Invites', path: '/products' },
  ];

  const handleNavClick = () => {
    setExpanded(false);
  };

  const handleSearchSubmit = (event?: React.FormEvent<HTMLFormElement>) => {
    if (event) event.preventDefault();
    const query = searchTerm.trim();

    if (!query) {
      setExpanded(false);
      navigate('/products');
      return;
    }

    const normalized = query.toLowerCase();
    const exactMatch = searchableProducts.find((product) =>
      product.name.toLowerCase() === normalized ||
      product.id.toLowerCase() === normalized ||
      product.category.toLowerCase() === normalized
    );

    const partialMatch = searchableProducts.find((product) =>
      product.name.toLowerCase().includes(normalized) ||
      product.category.toLowerCase().includes(normalized) ||
      product.id.toLowerCase().includes(normalized)
    );

    setExpanded(false);

    if (exactMatch) {
      navigate(exactMatch.path);
      return;
    }

    if (partialMatch) {
      navigate(partialMatch.path);
      return;
    }

    navigate(`/products?search=${encodeURIComponent(query)}`);
  };

  const handleSearchTermChange = (value: string) => {
    setSearchTerm(value);
    const trimmed = value.trim();
    if (!trimmed) {
      navigate('/products', { replace: true });
      return;
    }
    navigate(`/products?search=${encodeURIComponent(trimmed)}`, { replace: true });
  };

  useEffect(() => {
    if (location.pathname !== '/products') return;
    const queryValue = new URLSearchParams(location.search).get('search') || '';
    setSearchTerm(queryValue);
  }, [location.pathname, location.search]);

  const handleNavbarLogout = () => {
    localStorage.clear();
    clearCart();
    onLogout();
    setExpanded(false);
    navigate('/login');
  };

  const isActive = (path: string): boolean => {
    return location.pathname === path;
  };

  return (
    <>
      <div
        style={{
          background: 'linear-gradient(90deg, #e91e8c 0%, #3b5bdb 100%)',
          color: '#fff',
          overflow: 'hidden',
          position: 'relative',
          padding: '8px 0',
          boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
        }}
      >
        <style>{`
          @keyframes marquee-scroll {
            0% { transform: translateX(100%); }
            100% { transform: translateX(-100%); }
          }
        `}</style>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'clamp(6px, 2vw, 10px)',
            whiteSpace: 'nowrap',
            animation: 'marquee-scroll 30s linear infinite',
            fontSize: 'clamp(10px, 2vw, 13px)',
            fontWeight: 700,
            letterSpacing: '0.3px',
            width: 'max-content',
            minWidth: '100%',
          }}
        >
          <span style={{ marginLeft: 'clamp(6px, 2vw, 12px)' }}>
            <i className="fas fa-bolt me-1" style={{ fontSize: 'clamp(9px, 1.5vw, 11px)' }}></i>
            <span className="d-none d-sm-inline">3 Hours Delivery Available on Selected Printing Orders</span>
            <span className="d-sm-none">3 Hrs Delivery</span>
          </span>
          <span style={{ marginLeft: 'clamp(12px, 3vw, 24px)' }}>
            <i className="fas fa-phone me-1" style={{ fontSize: 'clamp(9px, 1.5vw, 11px)' }}></i>
            <span className="d-none d-sm-inline">Call: +91 91508 08130</span>
            <span className="d-sm-none">+91 91508 08130</span>
          </span>
          {/* <span style={{ marginLeft: '24px' }}>
            <i className="fas fa-bolt me-2"></i> 3 Hours Delivery Available on Select Printing Orders
          </span>
          <span style={{ marginLeft: '24px' }}>
            <i className="fas fa-clock me-2"></i> Fast Turnaround • Premium Print Quality • WhatsApp for Urgent Orders
          </span> */}
        </div>
      </div>
      <Navbar
        bg="white"
        expand="lg"
        sticky="top"
        style={{ boxShadow: '0 2px 15px rgba(0,0,0,0.08)', padding: '0 0', height: 'auto' }}
        expanded={expanded}
        onToggle={setExpanded}
      >
        <Container fluid style={{ padding: 'clamp(8px, 2vw, 12px) 5%' }}>
          <Navbar.Brand
            as={Link}
            to="/"
            className="d-flex align-items-center gap-2"
            style={{ textDecoration: 'none', flexShrink: 0 }}
            onClick={handleNavClick}
          >
            <img
              src={logo}
              alt="Prathitha Logo"
              className="navbar-brand-logo"
              style={{ objectFit: 'contain', borderRadius: 8, background: '#1a1a2e', padding: 2, width: 'clamp(40px, 8vw, 48px)', height: 'clamp(40px, 8vw, 48px)' }}
            />
            <div style={{ lineHeight: 1.1 }}>
              <span className="brand-title" style={{ fontSize: 'clamp(12px, 2.5vw, 14px)', fontWeight: 700 }}>Prathitha</span>
              <span className="brand-subtitle" style={{ fontSize: 'clamp(9px, 1.8vw, 11px)', fontWeight: 600 }}>Digital Printers</span>
            </div>
          </Navbar.Brand>

          {/* Desktop Search Bar - Centered */}
          <div className="d-none d-lg-flex align-items-center flex-grow-1 mx-4" style={{ minWidth: 280, maxWidth: 500, width: '100%' }}>
            <Form className="w-100" onSubmit={handleSearchSubmit}>
              <div
                className="navbar-search-wrapper"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  background: '#f5f5f5',
                  border: 'none',
                  borderRadius: '24px',
                  padding: 'clamp(6px, 1.5vw, 10px) clamp(10px, 2vw, 16px)',
                  transition: 'all 0.25s ease',
                  boxShadow: 'none',
                }}
                onFocus={(e) => {
                  const el = e.currentTarget;
                  el.style.background = '#fff';
                  el.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                }}
                onBlur={(e) => {
                  const el = e.currentTarget;
                  el.style.background = '#f5f5f5';
                  el.style.boxShadow = 'none';
                }}
              >
                <i className="fas fa-search" style={{ color: '#999', fontSize: 'clamp(11px, 2vw, 14px)', flexShrink: 0 }}></i>
                <Form.Control
                  type="search"
                  placeholder="Search"
                  aria-label="Search products"
                  value={searchTerm}
                  onChange={(e) => handleSearchTermChange(e.target.value)}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    boxShadow: 'none',
                    outline: 'none',
                    fontSize: 'clamp(12px, 2vw, 14px)',
                    padding: '0 10px',
                    flex: 1,
                    minWidth: 0,
                    color: '#333',
                  }}
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => { handleSearchTermChange(''); }}
                    style={{ background: 'none', border: 'none', color: '#999', padding: '0 6px', lineHeight: 1, cursor: 'pointer', flexShrink: 0 }}
                    aria-label="Clear search"
                  >
                    <i className="fas fa-times" style={{ fontSize: '12px' }}></i>
                  </button>
                )}
              </div>
            </Form>
          </div>

          {/* Mobile Cart & Toggle Group */}
          <div className="d-flex d-lg-none align-items-center gap-3">
            {isAuthenticated && localStorage.getItem('role') !== 'admin' && (
              <button
                onClick={() => { handleNavClick(); setShowCart(true); }}
                className="btn btn-link text-dark nav-link-custom d-flex align-items-center position-relative border-0 p-1"
                style={{ textDecoration: 'none', boxShadow: 'none', color: '#1e293b' }}
                title="View Cart"
              >
                <i className="fas fa-shopping-cart" style={{ fontSize: '1.25rem' }}></i>
                {cartItems.length > 0 && (
                  <Badge
                    pill
                    bg="danger"
                    className="position-absolute"
                    style={{
                      fontSize: '0.65rem',
                      padding: '0.25em 0.5em',
                      top: '-6px',
                      right: '-6px',
                      transform: 'translate(20%, -20%)'
                    }}
                  >
                    {cartItems.length}
                  </Badge>
                )}
              </button>
            )}
            <Navbar.Toggle aria-controls="main-nav" className="ms-1" />
          </div>

          <Navbar.Collapse id="main-nav">
            {/* Mobile Search Bar */}
            <div className="d-lg-none w-100 px-2 mb-3">
              <Form onSubmit={handleSearchSubmit}>
                <div
                  className="navbar-search-wrapper"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    background: '#f5f5f5',
                    border: 'none',
                    borderRadius: '24px',
                    padding: 'clamp(6px, 1.5vw, 10px) clamp(10px, 2vw, 14px)',
                    boxShadow: 'none',
                  }}
                >
                  <i className="fas fa-search" style={{ color: '#999', fontSize: 'clamp(11px, 2vw, 14px)', flexShrink: 0 }}></i>
                  <Form.Control
                    type="search"
                    placeholder="Search"
                    aria-label="Search products"
                    value={searchTerm}
                    onChange={(e) => handleSearchTermChange(e.target.value)}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      boxShadow: 'none',
                      outline: 'none',
                      fontSize: 'clamp(12px, 2vw, 14px)',
                      padding: '0 10px',
                      flex: 1,
                      minWidth: 0,
                      color: '#333',
                    }}
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={() => { handleSearchTermChange(''); }}
                      style={{ background: 'none', border: 'none', color: '#999', padding: '0 6px', lineHeight: 1, cursor: 'pointer', flexShrink: 0 }}
                      aria-label="Clear search"
                    >
                      <i className="fas fa-times" style={{ fontSize: '12px' }}></i>
                    </button>
                  )}
                </div>
              </Form>
            </div>

            <Nav className="align-items-center gap-1 gap-md-2 ms-auto">
              {navItems.map(({ label, path, icon }) => (
                <Nav.Link
                  key={path}
                  as={Link}
                  to={path}
                  className="nav-link-custom d-flex align-items-center gap-2"
                  style={{ fontSize: 'clamp(11px, 2.5vw, 13px)', padding: 'clamp(6px, 1.5vw, 8px) clamp(8px, 2vw, 12px)', whiteSpace: 'nowrap' }}
                  active={isActive(path)}
                  onClick={handleNavClick}
                >
                  <i className={icon} style={{ fontSize: 'clamp(12px, 2.5vw, 14px)', flexShrink: 0 }}></i>
                  <span>{label}</span>
                </Nav.Link>
              ))}





              {/* Shopping Cart Button */}
              {isAuthenticated && localStorage.getItem('role') !== 'admin' && (
                <button
                  onClick={() => { handleNavClick(); setShowCart(true); }}
                  className="btn btn-link text-dark nav-link-custom d-none d-lg-flex align-items-center position-relative p-1 border-0 ms-2"
                  style={{ textDecoration: 'none', boxShadow: 'none', color: '#1e293b' }}
                  title="View Cart"
                >
                  <i className="fas fa-shopping-cart" style={{ fontSize: '1.25rem' }}></i>
                  {cartItems.length > 0 && (
                    <Badge
                      pill
                      bg="danger"
                      className="position-absolute"
                      style={{
                        fontSize: '0.65rem',
                        padding: '0.25em 0.5em',
                        top: '-6px',
                        right: '-6px',
                        transform: 'translate(20%, -20%)'
                      }}
                    >
                      {cartItems.length}
                    </Badge>
                  )}
                </button>
              )}

              {!isAuthenticated ? (
                <>
                  <Nav.Link
                    as={Link}
                    to="/login"
                    className="nav-link-custom fw-semibold d-flex align-items-center gap-2 ms-1"
                    style={{ fontSize: 'clamp(11px, 2vw, 12px)', padding: 'clamp(6px, 1.5vw, 8px) clamp(8px, 2vw, 12px)', whiteSpace: 'nowrap' }}
                    active={isActive('/login')}
                    onClick={handleNavClick}
                  >
                    <i className="fas fa-sign-in-alt" style={{ fontSize: 'clamp(12px, 2vw, 14px)', flexShrink: 0 }}></i>
                    <span>Login</span>
                  </Nav.Link>
                  <button
                    className="btn-quote ms-1 ms-lg-2"
                    style={{ padding: 'clamp(6px, 1.5vw, 8px) clamp(8px, 2vw, 12px)', borderRadius: 6, fontSize: 'clamp(11px, 1.8vw, 12px)', whiteSpace: 'nowrap', flexShrink: 0 }}
                    onClick={() => { handleNavClick(); onGetQuote(); }}
                  >
                    <i className="fas fa-file-invoice me-1" style={{ fontSize: 'clamp(10px, 1.8vw, 12px)' }}></i>
                    <span>Quote</span>
                  </button>
                </>
              ) : (
                <>
                  <Dropdown align="end" className="ms-1 ms-lg-2">
                    <Dropdown.Toggle
                      id="user-dropdown"
                      className="d-flex align-items-center gap-2 fw-semibold border-0"
                      style={{
                        background: 'linear-gradient(90deg, rgba(233,30,140,0.08), rgba(59,91,219,0.08))',
                        color: '#1e293b',
                        borderRadius: '8px',
                        fontSize: 'clamp(11px, 2vw, 13.5px)',
                        padding: 'clamp(4px, 1vw, 6px) clamp(6px, 1.5vw, 10px)',
                        boxShadow: 'none',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white"
                        style={{
                          width: 'clamp(24px, 4vw, 28px)', height: 'clamp(24px, 4vw, 28px)', fontSize: 'clamp(9px, 1.5vw, 11px)',
                          background: 'linear-gradient(135deg, #e91e8c, #3b5bdb)',
                          flexShrink: 0,
                        }}
                      >
                        {(localStorage.getItem('username') || 'U').slice(0, 2).toUpperCase()}
                      </div>
                      <span className="d-none d-sm-inline">
                        {(localStorage.getItem('username') || '').slice(0, 10).replace(/\b\w/g, c => c.toUpperCase())}
                      </span>
                    </Dropdown.Toggle>

                    <Dropdown.Menu
                      className="shadow border-0 rounded-3 py-2 mt-1"
                      style={{ minWidth: '210px', border: '1px solid #f1f5f9' }}
                    >
                      {/* Header */}
                      <div className="px-3 pb-2 mb-1 border-bottom">
                        <div className="fw-bold text-dark" style={{ fontSize: 'clamp(12px, 2vw, 13.5px)' }}>
                          {(localStorage.getItem('username') || '').replace(/\b\w/g, c => c.toUpperCase())}
                        </div>
                        <div className="text-muted" style={{ fontSize: 'clamp(10px, 1.8vw, 11.5px)', textTransform: 'capitalize' }}>
                          {(role === 'admin' || role === 'admin-manager') ? 'Administrator' : role !== 'customer' ? `${role} Account` : 'Customer Account'}
                        </div>
                      </div>

                      {/* My Profile — both roles */}
                      <Dropdown.Item
                        as={Link}
                        to="/profile"
                        className="d-flex align-items-center gap-2 py-2 px-3"
                        style={{ fontSize: 'clamp(12px, 2vw, 13.5px)' }}
                        onClick={handleNavClick}
                      >
                        <i className="fas fa-user-circle" style={{ width: 16, color: 'var(--pink)' }}></i>
                        My Profile
                      </Dropdown.Item>

                      {/* My Tasks — employee/staff only (not customers) */}
                      {role !== 'customer' && (
                        <Dropdown.Item
                          as={Link}
                          to="/my-tasks"
                          className="d-flex align-items-center gap-2 py-2 px-3"
                          style={{ fontSize: 'clamp(12px, 2vw, 13.5px)' }}
                          onClick={handleNavClick}
                        >
                          <i className="fas fa-tasks" style={{ width: 16, color: 'var(--pink)' }}></i>
                          My Tasks
                        </Dropdown.Item>
                      )}

                      {/* Admin Dashboard — admin/manager/admin-manager */}
                      {(role === 'admin' || role === 'manager' || role === 'admin-manager') && (
                        <Dropdown.Item
                          as={Link}
                          to="/admin"
                          className="d-flex align-items-center gap-2 py-2 px-3"
                          style={{ fontSize: 'clamp(12px, 2vw, 13.5px)' }}
                          onClick={handleNavClick}
                        >
                          <i className="fas fa-tachometer-alt" style={{ width: 16, color: 'var(--pink)' }}></i>
                          Admin Dashboard
                        </Dropdown.Item>
                      )}

                      {/* My Orders — customer only */}
                      {role !== 'admin' && (
                        <Dropdown.Item
                          as={Link}
                          to="/my-orders"
                          className="d-flex align-items-center gap-2 py-2 px-3"
                          style={{ fontSize: 'clamp(12px, 2vw, 13.5px)' }}
                          onClick={handleNavClick}
                        >
                          <i className="fas fa-shopping-bag" style={{ width: 16, color: 'var(--pink)' }}></i>
                          My Orders
                        </Dropdown.Item>
                      )}

                      <Dropdown.Divider className="my-1" />

                      <Dropdown.Item
                        className="d-flex align-items-center gap-2 py-2 px-3 text-danger"
                        style={{ fontSize: 'clamp(12px, 2vw, 13.5px)' }}
                        onClick={handleNavbarLogout}
                      >
                        <i className="fas fa-sign-out-alt" style={{ width: 16 }}></i>
                        Logout
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </>
              )}
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* Product Categories Navbar Tier */}
      {/* <div style={{
        background: '#f9f9f9',
        borderBottom: '1px solid #f0f0f0',
        padding: '0 5%',
        overflowX: 'auto',
        overflowY: 'hidden',
        WebkitOverflowScrolling: 'touch',
      }}>
        <Container fluid style={{ padding: 0, display: 'flex', gap: '24px', minWidth: 'max-content' }}>
          {productCategories.map(({ label, path }) => (
            <Link
              key={label}
              to={path}
              className={`nav-link-category ${isActive(path) ? 'active' : ''}`}
              style={{
                textDecoration: 'none',
                color: isActive(path) ? '#e91e8c' : '#333',
                fontSize: '13px',
                fontWeight: isActive(path) ? 700 : 600,
                padding: '12px 0',
                borderBottom: isActive(path) ? '2px solid #e91e8c' : 'none',
                whiteSpace: 'nowrap',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#e91e8c';
              }}
              onMouseLeave={(e) => {
                if (!isActive(path)) {
                  e.currentTarget.style.color = '#333';
                }
              }}
              onClick={handleNavClick}
            >
              {label}
            </Link>
          ))}
        </Container>
      </div> */}
      <Offcanvas show={showCart} onHide={() => setShowCart(false)} placement="end" style={{ width: '400px', maxWidth: '100vw' }}>
        <Offcanvas.Header closeButton className="border-bottom pb-3">
          <Offcanvas.Title className="fw-bold text-dark d-flex align-items-center gap-2">
            <i className="fas fa-shopping-cart" style={{ color: 'var(--pink)' }}></i> Your Shopping Cart
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="d-flex flex-column h-100 p-4">
          {cartItems.length === 0 ? (
            <div className="text-center my-auto py-5 text-muted">
              <i className="fas fa-shopping-basket mb-3" style={{ fontSize: '48px', opacity: 0.5 }}></i>
              <h5 className="fw-bold">Your cart is empty</h5>
              <p className="small">Configure and add items to your cart to checkout.</p>
              <button
                className="btn btn-primary-custom py-2 px-4 mt-3"
                onClick={() => { setShowCart(false); navigate('/order'); }}
              >
                Configure Products
              </button>
            </div>
          ) : (
            <>
              <div className="flex-grow-1 overflow-auto pe-2" style={{ maxHeight: 'calc(100vh - 250px)' }}>
                {cartItems.map((item) => (
                  <div key={item.id} className="p-3 bg-light border border-light rounded-3 mb-3 text-start position-relative">
                    <button
                      className="btn btn-sm btn-link text-muted position-absolute top-0 end-0 mt-2 me-2 p-1"
                      onClick={() => removeFromCart(item.id)}
                      style={{ border: 'none', background: 'transparent' }}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                    <h6 className="fw-bold text-dark pe-3 mb-1">{item.productName}</h6>

                    {/* Render chosen specs */}
                    <div className="text-muted mb-2" style={{ fontSize: '11px', lineHeight: '1.4' }}>
                      {Object.entries(item.options).map(([key, val]) => (
                        <div key={key} className="text-capitalize">
                          {key.replace(/([A-Z])/g, ' $1')}: <span className="fw-semibold text-dark">{val}</span>
                        </div>
                      ))}
                    </div>

                    {item.designFileName && (
                      <div className="d-flex align-items-center gap-1 small text-primary mb-2" style={{ fontSize: '11.5px' }}>
                        <i className="far fa-file-image"></i>
                        <span className="text-truncate" style={{ maxWidth: '180px' }} title={formatDesignFileName(item.designFileName)}>
                          {formatDesignFileName(item.designFileName)}
                        </span>
                      </div>
                    )}

                    <div className="d-flex align-items-center justify-content-between border-top pt-2 mt-2">
                      <div className="d-flex align-items-center gap-1">
                        <button
                          className="btn btn-sm btn-outline-secondary py-0 px-2"
                          style={{ borderRadius: '4px' }}
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          disabled={item.quantity <= 1}
                        >
                          -
                        </button>
                        <span className="fw-bold px-1" style={{ fontSize: '13px' }}>{item.quantity}</span>
                        <button
                          className="btn btn-sm btn-outline-secondary py-0 px-2"
                          style={{ borderRadius: '4px' }}
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        >
                          +
                        </button>
                      </div>
                      <span className="fw-bold" style={{ fontSize: '14px', color: 'var(--pink)' }}>
                        ₹{item.estimatedPrice.toLocaleString('en-IN')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-top pt-3 mt-auto">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span className="fw-semibold text-muted">Total:</span>
                  <span className="fw-bold text-dark h4 mb-0">₹{cartTotal.toLocaleString('en-IN')}</span>
                </div>

                <button
                  className="w-100 py-3 fw-bold text-white btn-quote"
                  style={{ borderRadius: '10px' }}
                  onClick={() => {
                    setShowCart(false);
                    // Navigate to checkout in Order page
                    navigate('/order', { state: { goToCheckout: true } });
                  }}
                >
                  <i className="fas fa-shopping-bag me-1"></i> Proceed to Checkout
                </button>
              </div>
            </>
          )}
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default AppNavbar;
